import React, { useEffect, useRef, useState } from "react";
import REIToggablePanel from "../../components/ui/toggablePanel";
import { useHttpGets } from "../../services/httpService";
import { MEMBER_KEY, Messages, PAYMENT_MODES, CLASSIC_SITE_URL, TOKEN_KEY } from "../../utils/constants";
import { ApiEndPoints } from "../../utils/endpoints";
import { REIButton, REIDropDown, REIProgressSpinner, REIToastContainer } from "../../components/ui";
import { UpgradeToProfessional } from "../../components/shared";
import {
  formatCurrencyWithDecimal,
  formatDate,
  formatDayCycle,
  hideUpgradePanel,
  isStarterPlan,
} from "../../utils/commonUtil";
import ContactSummary from "./ContactSummary";
import Subscriptions from "./Subscription";
import PaymentMethods from "./PaymentMethods";
import Invoice from "./Invoice";
import AddPaymentMethod from "./AddPaymentMethod";
import ChangeEmail from "./ChangeEmail";
import { Toast } from "primereact/toast";
import ChangePassword from "./ChangePassword";

const MyAccount: React.FC = () => {
  const memberId = localStorage.getItem(MEMBER_KEY);
  const endpoint = `${ApiEndPoints.myaccount}/GetAccountInfo?memberId=${memberId}`;
  const [accountInfo, setAccountInfo] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>();
  const [paymentFormVisible, setPaymentFormVisible] = useState<boolean>(false);
  const [refreshPaymentData, setRefreshPaymentData] = useState<boolean>(false);
  const toastRef = useRef<Toast>(null);
  const paymentMode: number = 0;
  const newPmId = useRef<string>();
  const [isUpdateEnabled, setIsUpdateEnabled] = useState<boolean>(true);

  const isMyAccountDown: boolean = false;

  useEffect(() => {
    if (!isMyAccountDown) {
      useHttpGets(endpoint, undefined)
        .then((result) => {
          if (result?.data?.data?.length > 0) {
            setAccountInfo(result?.data?.data[0]);
          }
        })
        .catch((error) => {});
    }
  }, [refreshPaymentData, isMyAccountDown]);

  const renderAccountInfo = () => {
    const latestPayment = accountInfo?.payments?.data?.[0];

    return (
      <>
        <div className="text-center">{!accountInfo && <REIProgressSpinner />}</div>
        {accountInfo && (
          <div className="px-2">
            <div className="flex flex-col md:flex-row p-1 w-full">
              <div className="flex flex-col lg:flex-row w-1/2 gap-3">
                <span className="font-semibold">Account:</span>
                <label title={accountInfo.name} className="break-words truncate">
                  {accountInfo?.name ? accountInfo.name : "N/A"}
                </label>
              </div>
              <div className="flex flex-col md:flex-row w-1/2 gap-3">
                <div className="content-center text-nowrap">
                  <span className="font-semibold">Account Balance:</span>
                </div>
                <div className="text-wrap content-center truncate">
                  <label>{formatCurrencyWithDecimal(accountInfo.remaining_debit_memo_balance)}</label>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row p-1 w-full border-solid border border-x-0 border-b-0 border-gray-300">
              <div className="flex flex-col md:flex-row w-1/2 gap-3">
                <div className="text-nowrap content-center">
                  <span className="font-semibold">Credit Balance:</span>
                </div>
                <div className="text-wrap content-center truncate">
                  <label>{formatCurrencyWithDecimal(accountInfo.remaining_debit_memo_balance)}</label>
                </div>
              </div>
              <div className="flex flex-col md:flex-row w-1/2 gap-3">
                <div className="content-center text-nowrap">
                  <span className="font-semibold">Account Currency:</span>
                </div>
                <div className="text-wrap content-center truncate">
                  <label>{accountInfo.currency}</label>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row p-1 w-full border-solid border border-x-0 border-b-0 border-gray-300">
              <div className="flex flex-col md:flex-row w-1/2 gap-3">
                <div className="text-nowrap content-center">
                  <span className="font-semibold">Bill Cycle Day:</span>
                </div>
                <div className="text-wrap content-center truncate">
                  <label>{formatDayCycle(accountInfo.bill_cycle_day)}</label>
                </div>
              </div>
              <div className="flex flex-col md:flex-row w-1/2 gap-3">
                <div className="content-center text-nowrap">
                  <span className="font-semibold">Last Invoiced:</span>
                </div>
                <div className="text-wrap content-center truncate">
                  <label>{latestPayment?.created_stime || ""}</label>
                </div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row p-1 w-full border-solid border border-x-0 border-b-0 border-gray-300">
              <div className="flex flex-col md:flex-row w-1/2 gap-3">
                <div className="content-center">
                  <span className="font-semibold">Last Paid:</span>
                </div>
                <div className="text-wrap content-center truncate md:mb-1">
                  <label>{latestPayment?.payment_date ? formatDate(latestPayment.payment_date) : ""}</label>
                </div>
              </div>
              <div className="flex flex-col md:flex-row w-1/2 gap-3">
                <div className="content-center text-nowrap">
                  <span className="font-semibold">Last Payment:</span>
                </div>
                <div className="text-wrap content-center truncate md:mb-1">
                  <label>{latestPayment?.amount != null ? formatCurrencyWithDecimal(latestPayment.amount) : ""}</label>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const BillingInfoHeader = (
    <div className="flex flex-row bg-slate-600 border-solid border-slate-700 border-x border-y rounded-t-md">
      <div className="w-1/2 text-start text-white  font-bold text-sm ml-auto pl-3">
        <h3>Billing Info</h3>
      </div>
      <div className="w-1/2 text-end pl-3 content-center">
        <REIButton
          id="myacc-btn-updatecontact"
          disabled={!isUpdateEnabled}
          icon={`${isEditMode ? "pi pi-save" : "pi pi-pencil"}`}
          label={`${isEditMode ? "Save Changes" : "Update Contact"}`}
          classNames="border-gray-300 text-gray-700 font-bold text-sm mr-1 bg-gray-50"
          onClick={() => {
            setIsEditMode(!isEditMode);
          }}
        ></REIButton>
      </div>
    </div>
  );

  const PaymentInfoHeader = (
    <div className="bg-slate-600 border-solid border-slate-700 border-x border-y font-bold rounded-t-lg p-3 text-lg flex justify-between">
      <span className="text-white">Payment Methods</span>
      <div className="float-right  self-center">
        <REIDropDown
          id="myacc-drop-newpayment"
          placeholder="New Payment Method"
          value={paymentMode}
          className="text-xs h-10"
          label="name"
          data={PAYMENT_MODES}
          onSelectionChange={(e) => {
            setPaymentFormVisible(true);
          }}
          optionValue="code"
        ></REIDropDown>
      </div>
    </div>
  );

  const refreshData = () => {
    newPmId.current = undefined;
    setRefreshPaymentData((prev) => !prev);
  };

  const renderPaymentMethod = (isCardAdded: boolean, id?: string) => {
    setPaymentFormVisible(!paymentFormVisible);
    if (isCardAdded) {
      setRefreshPaymentData(!refreshPaymentData);
      toastRef.current?.show({
        severity: "success",
        summary: Messages.SUCCESS,
        detail: "Card added successfully.",
        life: 2000,
      });
    }
    newPmId.current = id;
  };

  const handleIsValid = (isValid: boolean) => {
    setIsUpdateEnabled(isValid);
  };

  const renderUpgradePanel = () => (
    <div className="flex flex-col md:flex-row md:items-center gap-4 p-3">
      <div className="flex-1">
        <h3 className="m-0 text-base font-bold text-slate-800">Upgrade to Professional Membership</h3>
        <p className="mt-1 mb-0 text-sm text-slate-600">
          Add nationwide{" "}
          <a href="../rp-data" target="_self">
            Cotality RP Data
          </a>{" "}
          (to provide price estimates, sales and rental history)
        </p>
      </div>
      <UpgradeToProfessional onUpgraded={refreshData} />
    </div>
  );

  return (
    <div className={isMyAccountDown ? "fixed inset-0 z-50 bg-slate-300" : "mt-4"}>
      {isMyAccountDown && (
        <div className="flex h-full w-full items-center justify-center p-4">
          <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-slate-100 bg-white text-center shadow-2xl">
            <div className="absolute left-0 top-0 h-2 w-full bg-gradient-to-r from-orange-400 to-amber-500"></div>
            <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-orange-50 blur-3xl opacity-50"></div>
            <div className="absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-amber-50 blur-3xl opacity-50"></div>

            <div className="relative z-10 px-8 py-12 md:px-12">
              <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-orange-50 shadow-sm ring-8 ring-orange-50/60">
                <i className="pi pi-exclamation-triangle text-5xl text-orange-500" />
              </div>

              <h2 className="mb-3 text-2xl font-extrabold text-slate-800 md:text-3xl">Page Under Maintenance</h2>

              <p className="mb-8 text-base leading-relaxed text-slate-500 md:text-lg">
                The My Accounts section is currently undergoing essential maintenance. You can still manage your account
                settings securely via our Classic Site.
              </p>

              <REIButton
                id="myacc-btn-classicsite"
                label="Go to Classic Site"
                icon="pi pi-arrow-right"
                iconPos="right"
                onClick={() => {
                  const url = `${CLASSIC_SITE_URL}/?AuthToken=${localStorage.getItem(TOKEN_KEY)}&MemberID=${localStorage.getItem(MEMBER_KEY)}`;
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
                classNames="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 border-none py-4 text-base font-bold shadow-lg shadow-orange-200 transition-all hover:-translate-y-1 hover:shadow-xl sm:w-auto sm:px-10 rounded-xl"
              />

              <p className="mt-8 text-sm text-slate-400">We apologize for the inconvenience.</p>
            </div>
          </div>
        </div>
      )}
      {!isMyAccountDown && (
        <>
          <REIToastContainer toastReference={toastRef}></REIToastContainer>
          {isStarterPlan() && !hideUpgradePanel && (
            <div className="flex-1 w-full mb-2">
              <REIToggablePanel
                id="myacc-pnl-upgrade"
                header="Membership Upgrade"
                istoggleable={false}
                defaultCollapse={false}
                contentComponent={renderUpgradePanel()}
              />
            </div>
          )}
          <div className="flex flex-col lg:flex-row flex-wrap gap-2">
            <div className="flex flex-col lg:flex-row lg:w-full gap-2">
              <div className="flex-1 lg:w-1/2 w-full">
                <REIToggablePanel
                  id="myacc-pnl-accountsummary"
                  header="Account Summary"
                  istoggleable={true}
                  defaultCollapse={false}
                  contentComponent={renderAccountInfo()}
                />
              </div>
              <div className="flex-1 lg:w-1/2 w-full">
                <REIToggablePanel
                  customHeader={BillingInfoHeader}
                  istoggleable={false}
                  defaultCollapse={false}
                  header=""
                  contentComponent={<ContactSummary isEditMode={isEditMode} isValid={handleIsValid}></ContactSummary>}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row flex-wrap lg:w-full gap-2 mt-2">
            <div className="flex-1 w-full" id="myacc-pnl-paymentmethods" data-testid="myacc-pnl-paymentmethods">
              <REIToggablePanel
                header=""
                customHeader={PaymentInfoHeader}
                istoggleable={true}
                defaultCollapse={false}
                contentComponent={
                  accountInfo && (
                    <PaymentMethods
                      onsuccess={refreshData}
                      refreshData={refreshPaymentData}
                      cardId={newPmId.current}
                      accountId={accountInfo.id}
                    ></PaymentMethods>
                  )
                }
              />
            </div>
            <div className="flex-1 lg:w-1/2 w-full">
              <REIToggablePanel
                header="Change Password"
                istoggleable={false}
                defaultCollapse={false}
                contentComponent={accountInfo && <ChangePassword />}
              />
            </div>
          </div>
          <div className="flex flex-col lg:flex-row flex-wrap lg:w-full gap-2 mt-2">
            <div className="flex flex-col lg:flex-row lg:w-full gap-2">
              <div className="flex-1 w-full">
                <REIToggablePanel
                  id="myacc-pnl-subscriptions"
                  header="Subscriptions"
                  istoggleable={true}
                  defaultCollapse={false}
                  contentComponent={<Subscriptions />}
                />
              </div>
              <div className="flex-1 lg:w-1/2 w-full">
                <REIToggablePanel
                  header="Change Email Address"
                  istoggleable={false}
                  defaultCollapse={false}
                  contentComponent={
                    accountInfo && (
                      <ChangeEmail emailId={JSON.parse(localStorage.getItem("user_detail")).EmailAddress} />
                    )
                  }
                />
              </div>
            </div>
          </div>

          <div className="flex-1 w-full mt-2 overflow-auto" id="myacc-pnl-invoices" data-testid="myacc-pnl-invoices">
            <REIToggablePanel
              header="Invoices"
              istoggleable={false}
              defaultCollapse={true}
              contentComponent={<Invoice />}
            />
          </div>
          {accountInfo && (
            <AddPaymentMethod
              onsuccess={renderPaymentMethod}
              isAddPaymentMethodMode={paymentFormVisible}
              accountId={accountInfo.id}
            ></AddPaymentMethod>
          )}
        </>
      )}
    </div>
  );
};

export default MyAccount;
